export type AudiencePerson = {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  licence_type?: string;
};

export type GroupedAudienceDetail = {
  license_emails?: {
    licence_type?: string;
    emails?: string[];
  } | Array<{ licence_type?: string; emails?: string[] }>;
  licence_emails?: {
    licence_type?: string;
    emails?: string[];
  } | Array<{ licence_type?: string; emails?: string[] }>;
  no_licence_emails?: {
    licence_type?: string;
    emails?: string[];
  } | Array<{ licence_type?: string; emails?: string[] }>;
};

export type PublicOrderAudienceResponse = {
  enduser_details?: AudiencePerson;
  brokerage_details?: AudiencePerson;
  signage_details?: AudiencePerson;
  signage_teammember_details?: AudiencePerson;
  brokerage_teammember_details?: AudiencePerson;
  grouped_details?: GroupedAudienceDetail[] | GroupedAudienceDetail;
  project_details?: {
    enduser_email?: string;
    payment_email?: string;
  };
  order_info?: {
    order_id?: string;
  };
};

const normalizeEmail = (value?: string): string => String(value || "").trim().toLowerCase();

export async function fetchPublicOrderAudienceData(
  signageId: string | undefined,
  orderId: string
): Promise<PublicOrderAudienceResponse | null> {
  const oid = String(orderId || "").trim();
  if (!oid) return null;

  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (!api) return null;

  const response = await fetch(
    // `${api}/Public/Signage/${encodeURIComponent(sid)}/order/${encodeURIComponent(oid)}`,
    `${api}/public/email/orders/${encodeURIComponent(oid)}/details`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch audience data (${response.status})`);
  }

  const payload = (await response.json()) as PublicOrderAudienceResponse;
  return payload && typeof payload === "object" ? payload : null;
}

export async function fetchPublicInstallationAudienceData(
  installationOrderId: string
): Promise<PublicOrderAudienceResponse | null> {
  const oid = String(installationOrderId || "").trim();
  if (!oid) return null;

  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (!api) return null;

  const response = await fetch(
    `${api}/public/email/installation-orders/${encodeURIComponent(oid)}/details`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch installation audience data (${response.status})`);
  }

  const payload = (await response.json()) as PublicOrderAudienceResponse;
  return payload && typeof payload === "object" ? payload : null;
}

export async function fetchPublicRemovalAudienceData(
  removalOrderId: string
): Promise<PublicOrderAudienceResponse | null> {
  const oid = String(removalOrderId || "").trim();
  if (!oid) return null;

  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (!api) return null;

  const response = await fetch(
    `${api}/public/email/removal-orders/${encodeURIComponent(oid)}/details`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch removal audience data (${response.status})`);
  }

  const payload = (await response.json()) as PublicOrderAudienceResponse;
  return payload && typeof payload === "object" ? payload : null;
}

export function collectAllAudienceEmails(payload?: PublicOrderAudienceResponse | null): string[] {
  if (!payload) return [];

  const rawEmails: string[] = [];
  const directPeople = [
    payload.enduser_details,
    payload.brokerage_details,
    payload.signage_details,
    payload.signage_teammember_details,
    payload.brokerage_teammember_details,
  ];

  directPeople.forEach((person) => {
    if (person?.email) rawEmails.push(person.email);
  });

  if (payload.project_details?.enduser_email) {
    rawEmails.push(payload.project_details.enduser_email);
  }
  if (payload.project_details?.payment_email) {
    rawEmails.push(payload.project_details.payment_email);
  }

  const grouped = Array.isArray(payload.grouped_details)
    ? payload.grouped_details
    : payload.grouped_details
      ? [payload.grouped_details]
      : [];

  const extractEmails = (
    entry?:
      | { emails?: string[] }
      | Array<{ emails?: string[] }>
      | null
  ): string[] => {
    if (!entry) return [];
    if (Array.isArray(entry)) return entry.flatMap((item) => item?.emails || []);
    return entry.emails || [];
  };

  grouped.forEach((group) => {
    extractEmails(group?.license_emails).forEach((email) => rawEmails.push(email));
    extractEmails(group?.licence_emails).forEach((email) => rawEmails.push(email));
    extractEmails(group?.no_licence_emails).forEach((email) => rawEmails.push(email));
  });

  const seen = new Set<string>();
  const deduped: string[] = [];
  rawEmails.forEach((email) => {
    const normalized = normalizeEmail(email);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push(String(email).trim());
  });

  return deduped;
}
